import { useState } from "react";
import { api, type Guest } from "./api";
import { Avatar, Field } from "./GuildhallParts";
type Reply = { identity: Guest | null; recoveryToken?: string };
export function AccountPanel({
    guest,
    busy,
    act,
    update,
    recovery,
    keepRecovery,
}: {
    guest: Guest | null;
    busy: boolean;
    act: (work: () => Promise<unknown>) => Promise<void>;
    update: (guest: Guest | null) => void;
    recovery: string | null;
    keepRecovery: (value: string | null) => void;
}) {
    const [mode, setMode] = useState<"login" | "register" | "profile">(
        guest?.registered ? "profile" : "register",
    );
    const [name, setName] = useState(
        guest?.profile?.displayName ?? guest?.name ?? "",
    );
    const [primary, setPrimary] = useState(
        guest?.profile?.primaryColor ?? "#b23a4e",
    );
    const [secondary, setSecondary] = useState(
        guest?.profile?.secondaryColor ?? "#d4ae62",
    );
    const [icon, setIcon] = useState<string | null | undefined>(undefined);
    const [iconError, setIconError] = useState("");
    const auth = async (form: HTMLFormElement) => {
        const data = new FormData(form);
        const reply = await api<Reply>(`/account/${mode}`, {
            loginName: data.get("loginName"),
            password: data.get("password"),
            displayName: data.get("displayName"),
        });
        update(reply.identity);
        keepRecovery(reply.recoveryToken ?? null);
        if (reply.identity) window.location.href = "/";
    };
    const upload = async (file: File) => {
        setIconError("");
        try {
            if (file.size > 10 * 1024 * 1024)
                throw new Error("Choose an image smaller than 10 MB.");
            if (!file.type.startsWith("image/"))
                throw new Error("Choose an image file.");
            const bitmap = await createImageBitmap(file);
            const canvas = document.createElement("canvas");
            const size = Math.min(256, Math.max(bitmap.width, bitmap.height));
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext("2d")!;
            const scale = Math.min(size / bitmap.width, size / bitmap.height);
            ctx.drawImage(
                bitmap,
                (size - bitmap.width * scale) / 2,
                (size - bitmap.height * scale) / 2,
                bitmap.width * scale,
                bitmap.height * scale,
            );
            bitmap.close();
            const data = canvas.toDataURL("image/png");
            if (data.length > 170000)
                throw new Error(
                    "This icon is too detailed. Choose a smaller image.",
                );
            setIcon(data);
        } catch (error) {
            setIconError(
                error instanceof Error
                    ? error.message
                    : "Could not read that image.",
            );
        }
    };
    return (
        <div className="guild-form" data-issue-private>
            {mode !== "profile" ? (
                <>
                    <div className="guild-segment">
                        <button
                            type="button"
                            aria-pressed={mode === "register"}
                            onClick={() => setMode("register")}
                        >
                            Join the guild
                        </button>
                        <button
                            type="button"
                            aria-pressed={mode === "login"}
                            onClick={() => setMode("login")}
                        >
                            Sign in
                        </button>
                    </div>
                    <form
                        className="guild-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            void act(() => auth(e.currentTarget));
                        }}
                    >
                        <p>
                            {mode === "register"
                                ? "Keep your seats, build your house, and make yourself at home. Any nonempty password is welcome."
                                : "Welcome back. Your house and seats are waiting."}
                        </p>
                        <Field label="Login name">
                            <input
                                name="loginName"
                                required
                                autoComplete="username"
                                maxLength={60}
                            />
                        </Field>
                        <Field label="Password">
                            <input
                                name="password"
                                type="password"
                                required
                                autoComplete={
                                    mode === "login"
                                        ? "current-password"
                                        : "new-password"
                                }
                            />
                        </Field>
                        {mode === "register" && (
                            <Field label="Display name">
                                <input
                                    name="displayName"
                                    defaultValue={
                                        guest?.name === "Guest"
                                            ? ""
                                            : guest?.name
                                    }
                                    autoComplete="nickname"
                                    maxLength={60}
                                />
                            </Field>
                        )}
                        <button className="guild-btn primary" disabled={busy}>
                            {mode === "register" ? "Create account" : "Sign in"}
                        </button>
                    </form>
                </>
            ) : (
                <>
                    <form
                        className="guild-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            void act(async () => {
                                update(
                                    await api<Guest>("/account/profile", {
                                        displayName: name,
                                        primaryColor: primary,
                                        secondaryColor: secondary,
                                        ...(icon === undefined
                                            ? {}
                                            : { iconDataUrl: icon }),
                                    }),
                                );
                            });
                        }}
                    >
                        <div className="guild-profile-preview">
                            <Avatar
                                large
                                name={name}
                                profile={{
                                    displayName: name,
                                    primaryColor: primary,
                                    secondaryColor: secondary,
                                    iconUrl:
                                        icon === undefined
                                            ? guest?.profile?.iconUrl
                                            : icon,
                                }}
                            />
                            <h3>{name}</h3>
                        </div>
                        <Field label="Display name">
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                maxLength={60}
                                autoComplete="nickname"
                            />
                        </Field>
                        <div className="guild-two-col">
                            <Field label="Primary color">
                                <input
                                    type="color"
                                    value={primary}
                                    onChange={(e) => setPrimary(e.target.value)}
                                />
                            </Field>
                            <Field label="Secondary color">
                                <input
                                    type="color"
                                    value={secondary}
                                    onChange={(e) =>
                                        setSecondary(e.target.value)
                                    }
                                />
                            </Field>
                        </div>
                        <Field label="Upload profile icon">
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                onChange={(e) => {
                                    if (e.target.files?.[0])
                                        void upload(e.target.files[0]);
                                }}
                            />
                        </Field>
                        {iconError && <p role="alert">{iconError}</p>}
                        <button
                            type="button"
                            className="guild-link"
                            onClick={() => setIcon(null)}
                        >
                            Use initials instead
                        </button>
                        <button className="guild-btn primary" disabled={busy}>
                            Save profile
                        </button>
                    </form>
                    <details>
                        <summary>Password &amp; recovery</summary>
                        <form
                            className="guild-form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                const form = e.currentTarget;
                                const data = new FormData(form);
                                void act(async () => {
                                    await api("/account/password", {
                                        currentPassword:
                                            data.get("currentPassword"),
                                        password: data.get("password"),
                                    });
                                    form.reset();
                                });
                            }}
                        >
                            <Field label="Current password">
                                <input
                                    name="currentPassword"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                />
                            </Field>
                            <Field label="New password">
                                <input
                                    name="password"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                />
                            </Field>
                            <button className="guild-btn" disabled={busy}>
                                Change password
                            </button>
                        </form>
                    </details>
                    <button
                        className="guild-link"
                        disabled={busy}
                        onClick={() =>
                            void act(async () => {
                                await api("/account/logout", {});
                                update(null);
                                keepRecovery(null);
                                window.location.href = "/";
                            })
                        }
                    >
                        Sign out
                    </button>
                </>
            )}
            {!guest?.registered && (
                <form
                    className="guild-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        const form = e.currentTarget;
                        void act(async () => {
                            const reply = await api<Reply>("/session", {
                                name: new FormData(form).get("name") || "Guest",
                            });
                            update(reply.identity);
                            keepRecovery(reply.recoveryToken ?? null);
                        });
                    }}
                >
                    <Field label="Guest display name">
                        <input
                            name="name"
                            defaultValue={guest?.name ?? "Guest"}
                            maxLength={60}
                        />
                    </Field>
                    <button className="guild-btn" disabled={busy}>
                        Continue as guest
                    </button>
                    <p className="guild-hint">
                        Guests can join rooms and create allowed tables. An
                        account is needed to create rooms.
                    </p>
                </form>
            )}
            <details>
                <summary>Keep or recover your identity</summary>
                {guest && (
                    <button
                        type="button"
                        className="guild-btn"
                        onClick={() =>
                            void act(async () => {
                                const token =
                                    recovery ??
                                    (
                                        await api<{ recoveryToken: string }>(
                                            "/session/recovery",
                                            {},
                                        )
                                    ).recoveryToken;
                                keepRecovery(token);
                                await navigator.clipboard.writeText(
                                    `${window.location.origin}/#recover=${encodeURIComponent(token)}`,
                                );
                            })
                        }
                    >
                        Copy private recovery link
                    </button>
                )}
                <form
                    className="guild-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        const token = new FormData(e.currentTarget).get(
                            "token",
                        );
                        void act(async () => {
                            const reply = await api<Reply>("/session/recover", {
                                token,
                            });
                            update(reply.identity);
                            keepRecovery(reply.recoveryToken ?? null);
                            window.location.href = "/";
                        });
                    }}
                >
                    <Field label="Recovery code">
                        <input name="token" required autoComplete="off" />
                    </Field>
                    <button className="guild-btn" disabled={busy}>
                        Recover identity
                    </button>
                </form>
            </details>
        </div>
    );
}
